import { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

export function LanguageSelector({ variant = "dark", align = "right" }) {
  const { lang, setLang, langs, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = langs.find((l) => l.code === lang) || langs[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.select")}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-selector"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors duration-300 focus-ring ${
          open ? "bg-white/10 text-white" : "text-zinc-300 hover:text-white hover:bg-white/5"
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="font-medium">{current.short}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="lang-menu"
          className={`absolute mt-2 w-44 glass-strong rounded-xl p-1.5 z-[120] ${align === "right" ? "right-0" : "left-0"}`}
        >
          {langs.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === lang}
              onClick={() => { setLang(l.code); setOpen(false); }}
              data-testid={`lang-option-${l.code}`}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-300 ${
                l.code === lang ? "bg-white/10 text-white" : "text-zinc-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1 text-left">{l.label}</span>
              {l.code === lang && <Check className="w-4 h-4 text-cyan-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
